import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Save, CheckCircle, Star, Eye, Compass, Heart, Crown, Video, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { draftStorage, formatDraftAge } from '../utils/draftStorage';
import {
  getQuestionMetadata,
  getQuestionByIndex,
  getResponseKey,
  migrateLegacyResponses
} from '../utils/questionIndexMapper';

interface DreamItem {
  id?: string;
  question: string;
  description: string;
  category: 'D' | 'R' | 'E' | 'A' | 'M';
}

interface DreamSheetData {
  [key: string]: string;
}

export const DREAM_QUESTIONS = {
  D: {
    title: 'Driving Motivations',
    subtitle: 'What drives you in your career journey',
    icon: <Eye className="h-6 w-6" />,
    color: 'bg-blue-500',
    questions: [
      "What aspects of your work provide you with the most satisfaction and fulfillment?",
      "What type of work engages you so deeply that you lose track of time?",
      "What professional accomplishment are you most proud of and why?",
      "What skills or activities energize you rather than drain your energy?",
      "If money were no object, what type of work would you still choose to do?"
    ]
  },
  R: {
    title: 'Role Preferences',
    subtitle: 'The type of roles that best align with your strengths',
    icon: <Compass className="h-6 w-6" />,
    color: 'bg-green-500',
    questions: [
      "Do you prefer roles with clearly defined responsibilities or those that allow you to define your own path?",
      "Would you rather be a specialist with deep expertise or a generalist with broad knowledge across multiple areas?",
      "Do you prefer working independently or as part of a collaborative team?",
      "What level of client or customer interaction do you prefer in your ideal role?",
      "Do you seek roles that are primarily strategic, tactical, or a blend of both?"
    ]
  },
  E: {
    title: 'Environment Fit',
    subtitle: 'Work settings where you thrive and perform your best',
    icon: <Star className="h-6 w-6" />,
    color: 'bg-purple-500',
    questions: [
      "What type of organizational culture helps you perform at your best?",
      "Do you prefer a structured, predictable work environment or one that is more flexible and dynamic?",
      "What physical work environment helps you be most productive (open office, private space, hybrid, remote, etc.)?",
      "How important is work-life balance to you, and what does that ideally look like?",
      "What characteristics would your ideal company or organization have in terms of size, mission, or values?"
    ]
  },
  A: {
    title: 'Ambitions & Aspirations',
    subtitle: 'Your long-term career goals and aspirations',
    icon: <Heart className="h-6 w-6" />,
    color: 'bg-red-500',
    questions: [
      "What is your ultimate career goal or aspiration that you're working toward?",
      "What specific skills, experiences, or credentials do you hope to gain in the next 1-3 years?",
      "Where do you see yourself professionally in 5 years? 10 years?",
      "What legacy or impact do you hope to leave through your professional work?",
      "What might be holding you back from pursuing your true career ambitions?"
    ]
  },
  M: {
    title: 'Management Style',
    subtitle: 'How you lead and prefer to be led',
    icon: <Crown className="h-6 w-6" />,
    color: 'bg-amber-500',
    questions: [
      "What type of management or leadership approach brings out your best work?",
      "What is your natural leadership style when guiding others?",
      "How do you prefer to receive feedback, direction, and recognition?",
      "How do you approach conflict resolution and difficult conversations?",
      "What role do you believe a manager should play in an employee's professional development?"
    ]
  }
};

interface DreamSheetProps {
  onComplete?: () => void;
}

const DreamSheet: React.FC<DreamSheetProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState<'D' | 'R' | 'E' | 'A' | 'M'>('D');
  const [responses, setResponses] = useState<DreamSheetData>({});
  const [isSaving, setIsSaving] = useState(false);
  const [dreamSheetId, setDreamSheetId] = useState<string | null>(null);
  const [savedSections, setSavedSections] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const loadingRef = useRef(false);

  // Add bypass mode for testing
  const [bypassMode, setBypassMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const responsesRef = useRef(responses);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    responsesRef.current = responses;
  }, [responses]);

  const saveDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.save('dream_sheet', responsesRef.current, user.id);
  }, [user?.id]);

  const loadDraft = useCallback(() => {
    if (!user?.id) return null;
    return draftStorage.load('dream_sheet', user.id);
  }, [user?.id]);

  const clearDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.remove('dream_sheet', user.id);
  }, [user?.id]);

  useEffect(() => {
    saveDraft();

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [responses, saveDraft]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 DreamSheet: Tab hidden, saving draft...');
        saveDraft();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveDraft]);

  // Memoized loading function to prevent multiple calls
  const loadDreamSheet = useCallback(async () => {
    if (!user?.id || hasLoadedRef.current || loadingRef.current) {
      console.log('DreamSheet: Skipping load - user:', !!user?.id, 'hasLoaded:', hasLoadedRef.current, 'loading:', loadingRef.current);
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setLoadError(null);

    try {
      console.log('DreamSheet: Loading DREAM sheet for user:', user.id);

      // Load DREAM sheet directly from database
      console.log('🔍 DreamSheet: Loading data from database...');

      // Get existing DREAM sheet for this user - use better error handling
      const { data: dreamSheets, error: sheetsError, count } = await supabase
        .from('dream_sheets')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .limit(1);

      if (sheetsError) {
        console.error('DreamSheet: Error loading sheets:', sheetsError);
        throw new Error(`Failed to load DREAM sheet: ${sheetsError.message}`);
      }

      const dreamSheetData = dreamSheets && dreamSheets.length > 0 ? dreamSheets[0] : null;

      // Get all items for this sheet if it exists
      let dreamItemsData: any[] = [];
      if (dreamSheetData) {
        const { data: items, error: itemsError } = await supabase
          .from('dream_items')
          .select('*')
          .eq('dream_sheet_id', dreamSheetData.id);

        if (itemsError) {
          console.error('DreamSheet: Error loading items:', itemsError);
          throw new Error(`Failed to load DREAM items: ${itemsError.message}`);
        }

        dreamItemsData = items || [];
      }

      console.log('🔍 DreamSheet: Loaded', dreamItemsData.length, 'items');

      if (dreamSheetData) {
        setDreamSheetId(dreamSheetData.id);
        console.log('✅ DreamSheet: Found existing sheet:', dreamSheetData.id);
      } else {
        // Create new sheet if none exists
        console.log('🆕 DreamSheet: Creating new sheet...');
        const { data: newSheet, error: createError } = await supabase
          .from('dream_sheets')
          .insert({ user_id: user.id })
          .select('id')
          .single();

        if (createError) {
          console.error('DreamSheet: Error creating sheet:', createError);
          throw new Error(`Failed to create DREAM sheet: ${createError.message || 'Unknown error'}`);
        }

        if (!newSheet || !newSheet.id) {
          console.error('DreamSheet: Sheet created but no ID returned');
          throw new Error('Failed to create DREAM sheet: No ID returned');
        }

        // Verify the sheet was actually created by querying it back
        const { data: verifySheet, error: verifyError } = await supabase
          .from('dream_sheets')
          .select('id')
          .eq('id', newSheet.id)
          .single();

        if (verifyError || !verifySheet) {
          console.error('DreamSheet: Sheet creation verification failed:', verifyError);
          throw new Error('Failed to verify DREAM sheet creation. Please try again.');
        }

        setDreamSheetId(newSheet.id);
        console.log('✅ DreamSheet: Created and verified new sheet:', newSheet.id);
      }

      // Process the items data
      console.log('📊 DreamSheet: Processing', dreamItemsData.length, 'items');

      const loadedResponses: DreamSheetData = {};
      const savedSects = new Set<string>();

      dreamItemsData.forEach((item: any) => {
        if (item.question && item.description) {
          // Try to get metadata for this question
          const metadata = getQuestionMetadata(item.question, DREAM_QUESTIONS);

          if (metadata) {
            // Use stable key based on category and index
            const key = getResponseKey(metadata.category, metadata.index);
            loadedResponses[key] = item.description;
            savedSects.add(metadata.category);
            console.log('📝 DreamSheet: Loaded response for:', metadata.category, 'index', metadata.index, '->', item.question.substring(0, 50) + '...');
          } else {
            // Legacy question - try to match it or keep as-is
            console.warn('⚠️ DreamSheet: Could not match question to current set:', item.question.substring(0, 50) + '...');
            // Store with original key for now - will be migrated on save
            loadedResponses[item.question] = item.description;
          }
        }
      });

      // Migrate any legacy keys to stable keys
      const migratedResponses = migrateLegacyResponses(loadedResponses, DREAM_QUESTIONS);

      const draft = loadDraft();
      if (draft && Object.keys(draft).length > Object.keys(migratedResponses).length) {
        const draftAge = draftStorage.getDraftAge('dream_sheet', user.id);
        if (draftAge && draftAge < 7 * 24 * 60 * 60 * 1000) {
          console.log('📝 DreamSheet: Restoring draft from', formatDraftAge(draftAge));
          const migratedDraft = migrateLegacyResponses(draft, DREAM_QUESTIONS);
          setResponses(migratedDraft);
          toast.info(`Draft restored with ${Object.keys(migratedDraft).length} responses from ${formatDraftAge(draftAge)}`);
          hasLoadedRef.current = true;
          return;
        }
      }

      setResponses(migratedResponses);
      setSavedSections(savedSects);
      hasLoadedRef.current = true;

      console.log('✅ DreamSheet: Final state - Responses:', Object.keys(migratedResponses).length, 'Saved sections:', Array.from(savedSects));

      if (Object.keys(migratedResponses).length > 0) {
        toast.success(`Loaded ${Object.keys(migratedResponses).length} DREAM responses from database`);
      }
    } catch (error) {
      console.error('DreamSheet: Error loading data:', error);
      setLoadError(error.message || 'Failed to load DREAM sheet data');
      toast.error(`Failed to load DREAM data: ${error.message}`);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [user?.id]);

  // Load existing DREAM sheet data
  useEffect(() => {
    if (!user?.id || hasLoadedRef.current || loadingRef.current) return;

    loadDreamSheet();
  }, [user?.id, loadDreamSheet]);

  const handleResponseChange = (question: string, value: string, category: string, index: number) => {
    // Use stable key instead of question text
    const key = getResponseKey(category, index);
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSection = async (section: 'D' | 'R' | 'E' | 'A' | 'M') => {
    if (!user || !dreamSheetId) {
      console.error('DreamSheet: Cannot save - missing user or sheet ID', { user: !!user, dreamSheetId });
      toast.error('Unable to save: Sheet not properly initialized. Please refresh the page and try again.');
      return;
    }

    setIsSaving(true);

    try {
      console.log('DreamSheet: Saving section:', section, 'for sheet:', dreamSheetId);

      // Pre-flight check: Verify the sheet still exists in database
      const { data: sheetCheck, error: checkError } = await supabase
        .from('dream_sheets')
        .select('id')
        .eq('id', dreamSheetId)
        .single();

      if (checkError || !sheetCheck) {
        console.error('DreamSheet: Sheet verification failed:', checkError);
        throw new Error('Your DREAM sheet could not be found. Please refresh the page.');
      }

      const sectionQuestions = DREAM_QUESTIONS[section].questions;
      const sectionResponses = sectionQuestions
        .map((question, index) => {
          const key = getResponseKey(section, index);
          const response = responses[key];
          return response && response.trim().length >= 10 ? {
            question,
            description: response,
            category: section,
            index
          } : null;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      console.log('DreamSheet: Section responses to save:', sectionResponses.length, 'items');

      // Delete existing items for this section
      const { error: deleteError } = await supabase
        .from('dream_items')
        .delete()
        .eq('dream_sheet_id', dreamSheetId)
        .in('question', sectionQuestions);

      if (deleteError) {
        console.error('DreamSheet: Error deleting existing items:', deleteError);
        throw new Error(`Failed to delete existing items: ${deleteError.message}`);
      }

      // Insert new responses for this section
      if (sectionResponses.length > 0) {
        const itemsToInsert = sectionResponses.map(response => ({
          dream_sheet_id: dreamSheetId,
          question: response.question,
          description: response.description,
          category: section
        }));

        console.log('DreamSheet: Inserting items:', itemsToInsert);

        const { data: insertedData, error: insertError } = await supabase
          .from('dream_items')
          .insert(itemsToInsert)
          .select('id');

        if (insertError) {
          console.error('DreamSheet: Insert error:', insertError);
          throw new Error(`Failed to save responses: ${insertError.message}`);
        }

        if (!insertedData || insertedData.length === 0) {
          console.warn('DreamSheet: No data returned from insert');
          throw new Error('Save operation did not return confirmation. Please verify your data was saved.');
        }

        console.log('DreamSheet: Successfully inserted', insertedData.length, 'items');
      } else {
        console.log('DreamSheet: No responses to save for section:', section);
      }

      setSavedSections(prev => new Set([...prev, section]));
      saveDraft();
      toast.success(`${DREAM_QUESTIONS[section].title} section saved successfully! (${sectionResponses.length} responses)`);
    } catch (error: any) {
      console.error('DreamSheet: Error saving section:', error);
      const errorMessage = error?.message || 'An unknown error occurred';
      toast.error(`Failed to save section: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const saveAllSections = async () => {
    for (const section of ['D', 'R', 'E', 'A', 'M'] as const) {
      await saveSection(section);
    }
    
    if (onComplete) {
      onComplete();
    }
  };

  const getCompletionPercentage = () => {
    const totalQuestions = Object.values(DREAM_QUESTIONS).reduce((sum, section) => sum + section.questions.length, 0);
    // Only count responses with meaningful content (>= 10 characters)
    let answeredQuestions = 0;
    Object.entries(DREAM_QUESTIONS).forEach(([category, config]) => {
      config.questions.forEach((_, index) => {
        const key = getResponseKey(category, index);
        if (responses[key] && responses[key].trim().length >= 10) {
          answeredQuestions++;
        }
      });
    });
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const getSectionCompletion = (section: 'D' | 'R' | 'E' | 'A' | 'M') => {
    const sectionQuestions = DREAM_QUESTIONS[section].questions;
    // Only count responses with meaningful content (>= 10 characters)
    const answeredQuestions = sectionQuestions.filter((_, index) => {
      const key = getResponseKey(section, index);
      return responses[key] && responses[key].trim().length >= 10;
    }).length;
    return Math.round((answeredQuestions / sectionQuestions.length) * 100);
  };

  const sections = Object.keys(DREAM_QUESTIONS) as Array<'D' | 'R' | 'E' | 'A' | 'M'>;
  const currentIndex = sections.indexOf(currentSection);
  const currentSectionData = DREAM_QUESTIONS[currentSection];

  // Retry function - defined at top level before any conditional returns
  const retryLoad = useCallback(() => {
    hasLoadedRef.current = false;
    loadingRef.current = false;
    setLoadError(null);
    setIsLoading(true);
    // Don't call loadDreamSheet directly - let the useEffect handle it
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your DREAM sheet...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
          <div className="bg-red-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading DREAM Sheet</h3>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <div className="space-y-2">
            <Button onClick={retryLoad}>
              Try Again
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
      {/* Course Banner */}
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white">
            <Video className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">
              Need Help Getting Started?
            </h3>
            <p className="text-sm text-blue-800 mb-4">
              We recommend completing our <strong>Career Foundation Course</strong> (6 videos, 42 minutes)
              before filling out your DREAM sheet. You'll learn how to envision your career future,
              articulate your preferences, and define meaningful goals.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/course')}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Take the Course
              </Button>
              <Button
                onClick={() => navigate('/user-guide')}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                View All Resources
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Header */}
      <div className="bg-white rounded-xl border border-border/40 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">DREAM Sheet</h2>
          <div className="text-sm text-muted-foreground">
            Overall Progress: {Object.keys(responses).filter(key => responses[key] && responses[key].trim().length >= 10).length} of {Object.values(DREAM_QUESTIONS).reduce((sum, section) => sum + section.questions.length, 0)} questions ({getCompletionPercentage()}% Complete)
          </div>
        </div>
        
        {/* Section Tabs */}
        <div className="flex flex-nowrap overflow-x-auto space-x-1 bg-gray-100 rounded-lg p-1">
          {sections.map((section) => {
            const sectionData = DREAM_QUESTIONS[section];
            const completion = getSectionCompletion(section);
            const isSaved = savedSections.has(section);
            
            return (
              <button
                key={section}
                onClick={() => setCurrentSection(section)}
                className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                  currentSection === section
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className={`p-1 rounded-full ${sectionData.color} text-white`}>
                  {React.cloneElement(sectionData.icon, { className: 'h-4 w-4' })}
                </div>
                <span className="hidden lg:inline">{sectionData.title}</span>
                <span className="lg:hidden">{section}</span>
                {completion > 0 && (
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                    {completion}%
                  </span>
                )}
                {isSaved && <CheckCircle className="h-4 w-4 text-green-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Section Content */}
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className={`p-3 rounded-full ${currentSectionData.color} text-white`}>
            {currentSectionData.icon}
          </div>
          <div>
            <h3 className="text-2xl font-bold">{currentSectionData.title}</h3>
            <p className="text-muted-foreground">{currentSectionData.subtitle}</p>
          </div>
        </div>

        <div className="space-y-6">
          {currentSectionData.questions.map((question, index) => {
            const key = getResponseKey(currentSection, index);
            return (
              <div key={key} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {index + 1}. {question}
                </label>
                <textarea
                  value={responses[key] || ''}
                  onChange={(e) => handleResponseChange(question, e.target.value, currentSection, index)}
                  placeholder="Share your thoughts and aspirations here... (optional)"
                  className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 resize-vertical"
                />
              </div>
            );
          })}
        </div>

        {/* Section Actions */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => saveSection(currentSection)}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Section'}
            </Button>
            
            {savedSections.has(currentSection) && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                Saved
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const prevIndex = Math.max(0, currentIndex - 1);
                  setCurrentSection(sections[prevIndex]);
                }}
                disabled={currentIndex === 0}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <span className="text-sm text-muted-foreground px-2">
                {currentIndex + 1} of {sections.length}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const nextIndex = Math.min(sections.length - 1, currentIndex + 1);
                  setCurrentSection(sections[nextIndex]);
                }}
                disabled={currentIndex === sections.length - 1}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Final Actions */}
      <div className="mt-6 flex justify-center">
        <div className="space-y-4 text-center">
          <Button
            onClick={saveAllSections}
            disabled={isSaving}
            size="lg"
            className="flex items-center gap-2"
          >
            <Save className="h-5 w-5" />
            Save All & Complete DREAM Sheet
          </Button>
          
          <div className="flex gap-2 justify-center">
            <Button
              onClick={() => setBypassMode(!bypassMode)}
              variant="outline"
              size="sm"
            >
              {bypassMode ? 'Exit' : 'Enter'} Bypass Mode
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      {getCompletionPercentage() > 0 && (
        <div className="mt-6 bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium mb-3">Your Progress</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {sections.map((section) => {
              const sectionData = DREAM_QUESTIONS[section];
              const completion = getSectionCompletion(section);
              
              return (
                <div key={section} className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${sectionData.color} text-white flex items-center justify-center`}>
                    {React.cloneElement(sectionData.icon, { className: 'h-6 w-6' })}
                  </div>
                  <div className="text-sm font-medium">{sectionData.title}</div>
                  <div className="text-xs text-muted-foreground">{completion}% complete</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DreamSheet;